import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MessageCircle } from 'lucide-react';
import ExpertAvatar from './ExpertAvatar';
import type { AikupItem } from '../data/aikupData';
import FeedbackModal from './FeedbackModal';
import { useAuth } from '../contexts/AuthContext';

interface ExpertVisionModalProps {
  isOpen: boolean;
  onClose: () => void;
  expert: 'dany' | 'yannick' | 'francois' | null;
  item: AikupItem | null;
  departmentKey?: string;
}

export default function ExpertVisionModal({ isOpen, onClose, expert, item, departmentKey }: ExpertVisionModalProps) {
  if (!expert || !item) return null;

  const { user } = useAuth();
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const expertConfig = {
    dany: {
      name: 'Dany',
      title: 'Président',
      color: 'orange',
      bgColor: 'from-orange-50 to-orange-100',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-800',
      vision: item.visionDany,
      score: item.scoreDany || 5,
      email: 'dany.bourassa@bzinc.ca',
    },
    yannick: {
      name: 'Dany',
      title: 'CA/CE',
      color: 'orange',
      bgColor: 'from-orange-50 to-orange-100',
      borderColor: 'border-orange-200',
      textColor: 'text-orange-800',
      vision: item.visionDany || item.visionYannick,
      score: item.scoreDany || item.scoreYannick || 5,
      email: 'dany.bourassa@bzinc.ca',
    },
    francois: {
      name: 'François',
      title: 'VP Technologique',
      color: 'blue',
      bgColor: 'from-blue-50 to-blue-100',
      borderColor: 'border-blue-200',
      textColor: 'text-blue-800',
      vision: item.visionFrancois,
      score: item.scoreFrancois || 5,
      email: 'francois.delisle@bzinc.ca',
    },
  };

  const config = expertConfig[expert];
  const currentEmail = user?.email?.toLowerCase() || '';
  const isDany = currentEmail === 'dany.bourassa@bzinc.ca';
  const leaderEmail = config.email?.toLowerCase();
  const canOpenFeedback =
    !!departmentKey && (isDany || (!!leaderEmail && currentEmail === leaderEmail));

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: 20, opacity: 0 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-8 max-w-3xl w-full relative shadow-2xl max-h-[90vh] overflow-y-auto"
          >
            {/* Header avec avatar de l'expert */}
            <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-200">
              <ExpertAvatar 
                name={config.name} 
                score={config.score}
                type={expert}
              />
              <div className="flex-1">
                <h3 className="font-display text-2xl text-gray-800">
                  {config.name}
                </h3>
                <p className={`text-sm ${config.textColor}`}>
                  {config.title}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-600" />
              </button>
            </div>

            {/* Titre du concept */}
            <div className="mb-6">
              <h4 className="text-xl font-semibold text-gray-800 mb-2">
                {item.title}
              </h4>
              <p className="text-sm text-gray-500">{item.category}</p>
            </div>

            {/* Vision de l'expert */}
            <div className={`bg-gradient-to-br ${config.bgColor} rounded-2xl p-6 ${config.borderColor} border-2`}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-2xl">💭</span>
                <h4 className="font-semibold text-gray-800">Ma vision :</h4>
              </div>
              {config.vision ? (
                <p className="text-gray-700 leading-relaxed italic whitespace-pre-line">
                  {config.vision}
                </p>
              ) : (
                <p className="text-gray-500 leading-relaxed italic text-center py-4">
                  📝 Ma vision personnelle de ce concept arrive bientôt...
                </p>
              )}
            </div>

            {/* Footer avec score + bouton commentaires */}
            <div className="mt-6 pt-4 border-t border-gray-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-sm text-gray-600">Importance pour moi :</span>
                <div className="flex items-center gap-2">
                  <div className="flex gap-0.5">
                    {[...Array(5)].map((_, index) => (
                      <svg
                        key={index}
                        className={`w-5 h-5 ${
                          index < config.score 
                            ? 'text-yellow-400' 
                            : 'text-gray-300'
                        }`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <span className="font-bold text-lg text-gray-800">
                    {config.score}/5
                  </span>
                </div>
              </div>

              {canOpenFeedback && (
                <button
                  type="button"
                  onClick={() => setIsFeedbackOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-indigo-500 text-white text-sm font-semibold hover:bg-indigo-600 transition-colors shadow-md"
                >
                  <MessageCircle className="w-4 h-4" />
                  Commentaires (Amélioration continue)
                </button>
              )}
            </div>
          </motion.div>

          {isFeedbackOpen && departmentKey && (
            <FeedbackModal
              isOpen={isFeedbackOpen}
              onClose={() => setIsFeedbackOpen(false)}
              departmentKey={departmentKey}
              contextLevel="CARD"
              cardId={item.id}
              contextLabel={`Carte: ${item.title} – ${config.name}`}
              leaderEmail={config.email}
            />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
